<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { mdiClose } from '@quasar/extras/mdi-v7';
import { useStrapiPost } from 'shared/composables/useStrapiApi';
import useValidators from 'src/shared/composables/useValidators';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import BInput from 'src/shared/components/base/BInput/BInput.vue';

const { t: $t } = useI18n();
const $q = useQuasar();
const { required, email: emailValidator } = useValidators();

// See SchoolRegisterSignInView: dark ink on the purple/white Flash palette.
const FIELD_SHADOW = '#1c1c1c';

const email = ref('');
const isSubmitting = ref(false);

const { execute, error } = useStrapiPost('auth/forgot-password');

async function onSubmit() {
  if (!email.value) return;
  isSubmitting.value = true;
  try {
    // `flow` is a FlashStudies extension to the core endpoint: it selects the
    // school-branded reset page for the link in the email. Teachers and students
    // are allowed; the backend silently ignores anyone else.
    await execute({ data: { email: email.value, flow: 'school' } });
    if (error.value) {
      const msg = (error.value as { message?: string })?.message;
      $q.notify({
        type: 'negative',
        message: msg || $t('FAILED_TO_SEND_RESET_EMAIL'),
      });
      return;
    }
    $q.notify({
      type: 'positive',
      message: $t('IF_EMAIL_EXISTS_RESET_LINK_SENT'),
    });
  } catch (err) {
    console.error($t('FORGOT_PASSWORD_ERROR'), err);
    $q.notify({
      type: 'negative',
      message: $t('FAILED_TO_SEND_RESET_EMAIL'),
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <q-card
    square
    flat
    class="full-width bg-transparent text-dark flash-auth-card"
  >
    <q-card-section class="column items-center q-pb-none">
      <q-img
        src="/img/flashLogo_dark.svg"
        no-spinner
        alt="FlashStudies"
        class="flash-auth-logo"
      />
      <h3
        class="text-h4 q-my-md q-px-sm text-center text-uppercase text-weight-bold"
      >
        {{ $t('FORGOT_PASSWORD') }}
      </h3>
      <p class="text-center q-mb-none">
        {{ $t('ENTER_YOUR_EMAIL_ADDRESS') }}
      </p>
    </q-card-section>

    <q-card-section
      class="q-mx-auto full-width"
      style="max-width: 400px"
    >
      <q-form
        class="q-px-sm q-pt-md q-gutter-y-lg"
        @submit.prevent="onSubmit"
      >
        <BInput
          v-model="email"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          type="email"
          lazy-rules
          :rules="[required, emailValidator]"
          :label="$t('EMAIL')"
          autocomplete="email"
          :shadow-color="FIELD_SHADOW"
          label-color="dark"
          class="bg-white"
        />

        <BButton
          unelevated
          color="secondary"
          :loading="isSubmitting"
          text-color="dark"
          :shadow-color="FIELD_SHADOW"
          class="full-width"
          :label="$t('RESET_PASSWORD')"
          type="submit"
        />
      </q-form>
    </q-card-section>

    <q-card-section class="row items-center justify-center">
      <q-btn
        flat
        size="sm"
        class="q-my-none q-py-none"
        color="dark"
        :to="{ name: 'school.login' }"
        :label="$t('BACK_TO_LOGIN')"
      />
    </q-card-section>
  </q-card>
</template>

<style scoped>
.flash-auth-card {
  /* Stack above the fixed decoration art. */
  position: relative;
  z-index: 1;
}

.flash-auth-logo {
  width: 11rem;
  max-width: 60vw;
}
</style>
